<script setup lang="ts">
import { withQuery } from 'ufo'
import { email, fallback, literal, object, optional, parse, picklist, pipe, string } from 'valibot'

definePageMeta({
  layout: 'login',
  name: 'login',
  colorMode: 'light',
})

useHead({
  htmlAttrs: {
    class: 'bg-blurple-500',
  },
})

const route = useRoute()

const query = computed(
  () => parse(
    object({
      email: fallback(optional(pipe(string(), email())), undefined),
      provider: fallback(optional(literal('basic')), undefined),
      ...redirectToQuerySchema.entries,

      // Expand this as needed if more error cases are added to the domain-router endpoint
      error: fallback(optional(picklist(['email', 'auth0'])), undefined),
    }),
    route.query,
  ),
)

onMounted(() => {
  if (!import.meta.client) {
    return
  }

  let visitCount = 1
  try {
    const previous = Number(sessionStorage.getItem('login.visit-count') ?? '0')
    visitCount = Number.isFinite(previous) && previous >= 0 ? previous + 1 : 1
    sessionStorage.setItem('login.visit-count', String(visitCount))
  }
  catch {}

  $fetch<unknown>('/api/_log/login-event', {
    method: 'POST',
    body: {
      event: 'login-page-mounted',
      hasProvider: !!query.value.provider,
      hasError: !!query.value.error,
      hasRedirectTo: !!query.value.redirectTo,
      visitCount,
      ...(query.value.email ? { email: query.value.email } : {}),
    } satisfies ClientLoginEvent,
  }).catch(() => {})
})

const showPasswordField = computed(() => query.value.provider === 'basic')

const passwordField = useTemplateRef('password')

watch(passwordField, (value) => {
  if (value?.inputRef) {
    value.inputRef.focus()
  }
})

const emailError = ref<string>()

const passwordError = ref<string>()

const basicAuth = useBasicAuth(passwordError)

async function handleLogin(event: Event) {
  const formData = new FormData(event.target as HTMLFormElement)
  try {
    const { password, email } = parse(loginBodySchema, Object.fromEntries(formData))

    // If there is no password yet, just let the form be submitted as-is. If there is a password, we want to prevent the default form submission and handle it via the mutation.
    if (password) {
      event.preventDefault()
      basicAuth.mutate({ password, email, redirectTo: query.value.redirectTo })
    }
  }
  catch {
    // Almost certainly an email validation error
    event.preventDefault()
    emailError.value = 'Ugyldig e-post'
  }
}

watch(() => query.value.error, (error) => {
  if (error === 'email') {
    emailError.value = 'Ugyldig e-post'
  }
  else if (error === 'auth0') {
    // Shown when login is cancelled or the account cannot be matched.
    emailError.value = 'Innloggingen ble ikke fullført. Prøv igjen, eller kontakt oss hvis det gjentar seg.'
  }
  else {
    emailError.value = undefined
  }
}, {
  immediate: true,
})

// FIXME: Texts from CMS
</script>

<template>
  <section class="full-width md:grid grid-cols-2 px-m sm:px-l md:px-11 gap-x-12 lg:gap-x-36">
    <form class="flex flex-col gap-l self-center my-2xl md:my-4xl" method="POST" :action="withQuery('/api/auth/domain-router', { redirectTo: query.redirectTo })" @submit="handleLogin">
      <h1 class="title-l">
        Logg inn
      </h1>

      

      <div class="flex flex-col gap-m">
        <input v-if="!!query.email" name="email" hidden readonly :value="query.email">
        <FormInput id="email" :disabled="!!query.email" :model-value="query.email" type="email" label="E-post" placeholder="E-postadresse..." :error="emailError" @input="emailError = undefined" />
        <FormInput v-if="showPasswordField" id="password" ref="password" type="password" label="Passord" placeholder="Passord..." :error="passwordError" @input="passwordError = undefined" />
      </div>

      <p v-if="!showPasswordField">
        Tilknyttet Feide? Bruk din Feide-epost for å logge inn.
      </p>

      <div class="flex items-center gap-m">
        <Button :disabled="!!(emailError || passwordError)" variant="primary" color="moss" type="submit" size="medium">
          {{ showPasswordField ? 'Logg\xa0inn' : 'Neste' }}
        </Button>

        <NuxtLink v-if="showPasswordField" :to="{ name: 'reset-password' }">
          Glemt passord?
        </NuxtLink>
      </div>

      <div v-if="showPasswordField" class="p-m bg-carbon-50 rounded-2xl">
          <p class="font-body font-normal text-sm">
            Helt ny på Juridika? Kontakt Juridika-administratoren i firmaet ditt for å opprette brukertilgang, eller send en epost til 
            <a class="font-body font-normal text-sm" href="mailto:post@juridika.no">post@juridika.no</a>
          </p>
      </div>
    </form>

    <IllustrationJustitia
      class="w-full h-[90%] self-end hidden md:block"
      weight-class="fill-polar-100 stroke-polar-100"
      body-class="fill-blurple-100"
      shadow-class="fill-blurple-200"
    />
  </section>
</template>
